import Deferred from './Deferred'
import Queue from './Queue'

type Result<A> = IteratorResult<A>

/** The result returned from closed iterators. */
const closedResult = Object.freeze({
  value: undefined as any,
  done: true,
})

/**
 * Async iterable iterator with a non-optional [[return]] method.
 */
interface BalancerAsyncIterableIterator<A> extends AsyncIterableIterator<A> {
  // TODO the result can be undefined as well
  return(value?: A): Promise<Result<A>>
}

/**
 * Balances a push queue with a pull queue. Pulling with an empty push
 * queue 
 */
export default class Balancer<A> implements AsyncIterableIterator<A> {
  /** XXX */
  readonly pushOverflow = new Queue<Result<A>>()
  /** XXX */
  readonly pullOverflow = new Queue<(a: Result<A>) => void>()
  /** XXX */
  closed = false

  /**
   * Pull a promise of the next [[IteratorResult]].
   */
  next(): Promise<Result<A>> {
    if (this.closed) {
      return Promise.resolve(closedResult)
    }
    return this.pushOverflow.dequeueDefault(
      (result: Result<A>) => Promise.resolve(result),
      () => {
        const { resolve, promise } = new Deferred<Result<A>>()
        this.pullOverflow.enqueue(resolve)
        return promise
      },
    )
  }

  /**
   * Push the next value.
   *
   * @throws Throws if the balancer is closed
   */
  push(value: A): void {
    if (this.closed) {
      throw Error('Iterator is closed')
    }
    const result: Result<A> = {
      value,
      done: false,
    }
    this.pullOverflow.dequeueDefault(resolve => resolve(result), () => void this.pushOverflow.push(result))
  }

  /**
   * Returns itself, since it's already an iterator;
   */
  [Symbol.asyncIterator]() {
    return this
  }

  /**
   * Closes the iterator; clears the queues and makes [[Balancer.next]] only
   * return [[closedResult]].
   * 
   * @param value The result value to be returned
   */
  async return(value?: A): Promise<Result<A>> {
    if (!this.closed) {
      this.closed = true
      // Clear the queues
      this.pushOverflow.length = 0
      this.pullOverflow.length = 0
    }
    return {
      done: true,
      value: value as any, // cast as any because the TS lib types are incorrect
    }
  }

  /**
   * Convert [[Balancer]] to a generic async iterable iterator to hide implementation details.
   * 
   * @param onReturn Optional callback for when the iterator is closed with [[Balancer.return]]
   */
  wrap(onReturn?: () => void): BalancerAsyncIterableIterator<A> {
    return {
      [Symbol.asyncIterator]: () => this,
      next: () => this.closed ? Promise.resolve(closedResult) : this.next(),
      return: async (value?: A) => {
        if (onReturn && !this.closed) {
          onReturn()
        }
        return this.return(value)
      }
    }
  }
}
